<template>
  <DetailsAsideLayout>
    <template v-slot:detailsContent>
      <div :class="$style.sectionRoot">
        <h2 :class="$style.sectionHeader">
          Package Settings
        </h2>
        <div :class="$style.sectionContent">
          MacOS packages must be accessible from a URL pointing to the individual packages
          in order to be listed in the JumpCloud Software Management admin interface.
        </div>
        <div
          v-for="(setting, index) in item.settings"
          :key="index"
        >
          <div :class="$style.inputRow">
            <div :class="$style.flexSingleWidth">
              <InputValidator
                v-model="setting.description"
                :config="{
                  attribute: 'description',
                  label: 'Software Description',
                  name: 'Software Description',
                  placeholder: 'Example: Google Chrome',
                  value: setting.description,
                  validate: 'required'
                }"
                :tooltip="descriptionTooltip"
                @InputValidatorModel="(value,key) => handleSettingsValueChange(value,key,index)"
              />
            </div>
          </div>
          <div :class="[$style.inputRow, $style.validateRow]">
            <div :class="$style.urlInput">
              <InputValidator
                v-model="setting.location"
                :config="{
                  label: 'Software Package URL',
                  name: 'Software Package URL',
                  placeholder: 'Example: https://SecureAmazonBucketSomewhere.com/321wersItsa',
                  validate: { url: { require_protocol: true }, required: true },
                  value: setting.location
                }"
                :tooltip="urlTooltip"
                @InputValidatorModel="handlePackageUrlFieldChange"
              />
            </div>
            <div :class="$style.validateBtn">
              <FlatActionButton
                :action="handleValidate"
                :disabled="disabledValidAction"
                text="Validate"
              />
            </div>
          </div>
        </div>
        <div :class="$style.underDescription">
          Package size must not exceed 500 MB.
        </div>
        <div :class="$style.divider" />
        <div :class="$style.autoPopulateInfo">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" :class="$style.infoIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="#4373c7" fill-rule="evenodd" d="M6.9 3.333c-.544 0-.9-.36-.9-.917a1.1 1.1 0 011.088-1.083c.524 0 .912.367.912.924 0 .62-.506 1.076-1.1 1.076m-2.037 6c-.408 0-.691-.215-.691-.698 0-.614.385-1.657.466-1.872L5.43 4.52c.08-.232-.022-.346-.113-.346-.418 0-.964 1.11-1.168 1.11-.08 0-.15-.09-.15-.167 0-.227.504-.764.648-.93.456-.484 1.045-.854 1.7-.854.487 0 1.012.334.6 1.569l-.815 2.481c-.07.191-.193.513-.193.716 0 .09.048.179.14.179.337 0 .958-1.086 1.12-1.086.058 0 .133.078.133.191 0 .37-1.323 1.95-2.47 1.95m6.332-6.345A5.973 5.973 0 009.012.805 5.868 5.868 0 006 0a5.868 5.868 0 00-3.012.805A5.973 5.973 0 00.805 2.988 5.871 5.871 0 000 6a5.87 5.87 0 00.805 3.012 5.974 5.974 0 002.183 2.184A5.87 5.87 0 006 12a5.87 5.87 0 003.012-.804 5.974 5.974 0 002.183-2.184C11.732 8.092 12 7.089 12 6a5.869 5.869 0 00-.805-3.012"/></svg>
          Software name and version will be auto-populated during location validation
        </div>
        <div :class="$style.inputRow">
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="item.displayName"
              :config="{
                attribute: 'displayName',
                disabled: true,
                label: 'Software Name',
                name: 'Software Name',
                placeholder: 'Chrome',
                value: item.displayName,
              }"
            />
          </div>
        </div>
        <div
          v-for="setting in item.settings"
          :key="setting.packageId"
          :class="$style.inputRow"
        >
          <div :class="$style.flexSingleWidth">
            <InputValidator
              v-model="setting.packageVersion"
              :config="{
                attribute: 'packageVersion',
                disabled: true,
                label: 'Software Version',
                name: 'Software Version',
                placeholder: '90.0.4430.85',
                value: setting.packageVersion,
              }"
            />
          </div>
        </div>
      </div>
    </template>
  </DetailsAsideLayout>
</template>

<script>
import { cloneDeep } from 'lodash';
import { mapActions, mapState } from 'vuex';
import { notify } from 'client/util';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import CustomAppsValidationUrlModal from 'client/SoftwareApps/CustomAppsValidationUrlModal.vue';
import DetailsAsideLayout from 'client/components/AsidePanel/Details/DetailsAsideLayout.vue';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import InputValidator from 'client/components/FormValidation/InputValidator';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalEventBus from 'components/Modal/ModalEventBus';

const softwareAppsModel = StoreModelMap[ItemTypeConsts.softwareApps];

const { error, success } = Constants;

export default {
  name: 'CustomAppsDetails',

  components: {
    DetailsAsideLayout,
    FlatActionButton,
    InputValidator,
  },

  ModalEventBus,
  notify,

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  inject: ['$validator'],

  data() {
    return {
      descriptionTooltip: {
        text: 'Your unique display name for the managed software.',
      },
      urlTooltip: {
        text: `The URL for your custom package. The URL must meet these requirements:
        <ul>
          <li>Valid and accessible</li>
          <li>Resolves to a .pkg package file</li>
          <li>Response size is greater than 0</li>
          <li>Package will be read to calculate the file checksum</li>
          <li>Package will be opened to read app details</li>
        </ul>
        If a third-party package URL does not indicate the application version (for example, https://host.com/path/application.pkg), consider hosting these packages yourself to control the package version you provide to your users. See <a rel="noopener noreferrer" target="_blank" href="https://jumpcloud-support.force.com/support/s/article/Software-Management-Mac-OS">Software Management: MacOS</a>.`,
      },
      customPackageCheckbox: false,
      customPackageKbArticle: KbArticleUrls.managedSoftwareCustomPackage,
      urlValidate: '',
      disabledValidAction: true,
      softwareVersion: '',
      softwareName: '',
    };
  },

  computed: {
    ...mapState(softwareAppsModel, ['item']),
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    ...mapActions(softwareAppsModel, [
      'validateUrl',
    ]),

    async handlePackageUrlFieldChange(key) {
      await this.$validator.validate('Software Package URL');

      this.disabledValidAction = this.v2Errors.any() === true
      && (this.v2Fields['Software Package URL'] && this.v2Fields['Software Package URL'].valid === false);

      if (!this.disabledValidAction) {
        this.urlValidate = key;
      }
    },

    handleSettingsValueChange(value, key, index) {
      const updatedSettings = cloneDeep(this.item.settings);
      updatedSettings[index][key] = value;

      this.setItemField({ key: 'settings', value: updatedSettings });
    },

    handleValidate() {
      this.$options.ModalEventBus.open({
        component: CustomAppsValidationUrlModal,
        preventCloseOnOverlayClick: true,
      });

      this.validateUrl(this.urlValidate)
        .then(() => {
          this.$options.notify(success, 'Software package URL validated!');
          this.$emit('enableCta');
        }).catch(() => {
          this.$options.notify(error, 'Unable to validate software package URL! Check the URL package requirements.');
        }).finally(() => {
          this.$options.ModalEventBus.close();
        });
    },
  },
};
</script>

<style module>
@import '../css/flex.css';
@import '../css/sections.css';

@value (slowEaseFade) from '~css/transitions.css';
@value (jcGrey200, jcGrey600) from '~css/brand-colors.css';

.autoPopulateInfo {
  font-size: 1rem;
  margin: 15px 0;
}

.divider {
  border: 1px solid jcGrey200;
  width: 100%;
}

.infoIcon {
  display: inline-block;
  height: 15px;
  margin-right: 5px;
  vertical-align: middle;
  width: 15px;
}

.slideTransition,
.slideTransition-enter-active,
.slideTransition-leave-active {
  transition: slowEaseFade;
}

.slideTransition-enter,
.slideTransition-leave-to {
  opacity: 0;
  transform: translateY(-1em);
}

.underDescription {
  font-size: 0.85rem;
  margin-bottom: 20px;
  padding-left: 5px;
}

.urlInput {
  flex: 3;
}

.validateBtn {
  margin-bottom: 17px;
}

.validateRow {
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}
</style>
